import React, { useState, useEffect, useCallback } from 'react';
import { FaBrain, FaPlus, FaEdit, FaTrash, FaSearch, FaCheck, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { supabase } from '../lib/supabase';

const EMPTY_FORM = { question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'a' };

const AdminQuiz = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('quiz_questions')
      .select('*')
      .order('created_at', { ascending: false });
    setQuestions(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const filtered = questions.filter(q =>
    q.question.toLowerCase().includes(search.toLowerCase()) ||
    q.option_a.toLowerCase().includes(search.toLowerCase()) ||
    q.option_b.toLowerCase().includes(search.toLowerCase()) ||
    q.option_c.toLowerCase().includes(search.toLowerCase()) ||
    q.option_d.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (q) => {
    setForm({
      question: q.question,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d,
      correct_option: q.correct_option,
    });
    setEditingId(q.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.option_a.trim() || !form.option_b.trim() || !form.option_c.trim() || !form.option_d.trim()) return;
    setSaving(true);

    if (editingId) {
      const { error } = await supabase.from('quiz_questions').update(form).eq('id', editingId);
      if (!error) {
        setQuestions(prev => prev.map(q => q.id === editingId ? { ...q, ...form } : q));
      }
    } else {
      const { data, error } = await supabase.from('quiz_questions').insert(form).select();
      if (!error && data) {
        setQuestions(prev => [data[0], ...prev]);
      }
    }

    setSaving(false);
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus soal ini?')) return;
    const { error } = await supabase.from('quiz_questions').delete().eq('id', id);
    if (!error) setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const correctLabel = { a: 'A', b: 'B', c: 'C', d: 'D' };

  return (
    <div className="font-inter bg-gray-50 dark:bg-dark-900 min-h-screen transition-colors">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <FaBrain className="text-gold-400" /> Kelola Soal Kuis
            </h1>
            <p className="text-gray-300">Tambah, edit, dan hapus soal trivia PIMNAS</p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-sm"
          >
            <FaPlus /> Tambah Soal
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        {/* Stats */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-4 mb-6 flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Total: <span className="font-bold text-gray-900 dark:text-white">{questions.length} soal</span>
          </span>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cari soal..."
              className="pl-9 pr-4 py-2 border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-48 sm:w-64"
            />
          </div>
        </div>

        {/* Add / Edit Form */}
        {showForm && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 mb-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">
              {editingId ? '✏️ Edit Soal' : '➕ Tambah Soal Baru'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pertanyaan</label>
                <textarea
                  value={form.question}
                  onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="Tulis pertanyaan..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['a', 'b', 'c', 'd'].map(key => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Opsi {key.toUpperCase()} {form.correct_option === key && <span className="text-emerald-500 text-xs ml-1">✓ Jawaban Benar</span>}
                    </label>
                    <input
                      type="text"
                      value={form[`option_${key}`]}
                      onChange={e => setForm(f => ({ ...f, [`option_${key}`]: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      placeholder={`Opsi ${key.toUpperCase()}...`}
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Jawaban Benar</label>
                <div className="flex gap-3">
                  {['a', 'b', 'c', 'd'].map(key => (
                    <button
                      key={key}
                      onClick={() => setForm(f => ({ ...f, correct_option: key }))}
                      className={`w-12 h-12 rounded-xl font-bold text-sm transition-all ${
                        form.correct_option === key
                          ? 'bg-emerald-500 text-white ring-2 ring-emerald-400 shadow-lg'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                      }`}
                    >
                      {key.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !form.question.trim() || !form.option_a.trim() || !form.option_b.trim() || !form.option_c.trim() || !form.option_d.trim()}
                  className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 px-6 rounded-xl transition-all disabled:opacity-40 flex items-center gap-2 text-sm"
                >
                  {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaCheck />}
                  {editingId ? 'Simpan Perubahan' : 'Simpan Soal'}
                </button>
                <button
                  onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
                  className="bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 font-medium py-2.5 px-6 rounded-xl transition-all flex items-center gap-2 text-sm"
                >
                  <FaTimes /> Batal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Memuat soal...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">📝</div>
            <p className="text-gray-500 dark:text-gray-400">{search ? 'Tidak ada soal yang cocok' : 'Belum ada soal. Tambahkan soal pertama!'}</p>
          </div>
        ) : (
          <div className="space-y-3 pb-10">
            {filtered.map((q, idx) => (
              <div key={q.id} className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                {/* Question header */}
                <div
                  className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors"
                  onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                >
                  <span className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed">{q.question}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Jawaban: <span className="font-bold text-emerald-600">{correctLabel[q.correct_option]}</span> — {q[`option_${q.correct_option}`]}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={e => { e.stopPropagation(); openEdit(q); }}
                      className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40 flex items-center justify-center transition-colors"
                    >
                      <FaEdit className="text-xs" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); handleDelete(q.id); }}
                      className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40 flex items-center justify-center transition-colors"
                    >
                      <FaTrash className="text-xs" />
                    </button>
                    {expandedId === q.id ? <FaChevronUp className="text-gray-400 text-xs" /> : <FaChevronDown className="text-gray-400 text-xs" />}
                  </div>
                </div>

                {/* Expanded options */}
                {expandedId === q.id && (
                  <div className="border-t border-gray-100 dark:border-dark-700 px-4 py-3 bg-gray-50/50 dark:bg-dark-700/30">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {['a', 'b', 'c', 'd'].map(key => (
                        <div
                          key={key}
                          className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${
                            q.correct_option === key
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                              : 'bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-600'
                          }`}
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            q.correct_option === key ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-dark-600 text-gray-500 dark:text-gray-400'
                          }`}>
                            {key.toUpperCase()}
                          </span>
                          <span className={`${q.correct_option === key ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                            {q[`option_${key}`]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuiz;
